import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Swords, Crown, Zap, Info } from "lucide-react";
import { api } from "../lib/api";
import { Loader, Empty } from "../components/UI";
import { ClubCrest } from "../components/ClubCrest";
import { StatusPill } from "./CupPage";
import { useEventStream } from "../lib/sse";

const CONTAINER = "mx-auto max-w-[1300px] px-4 sm:px-8 lg:px-12";

export default function TiePage() {
  const { tieId } = useParams();
  const [data, setData] = useState(null);
  const [nf, setNf] = useState(false);
  const load = useCallback(() => {
    api.get(`/cup/ties/${tieId}`).then(({ data }) => setData(data)).catch(() => setNf(true));
  }, [tieId]);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  useEventStream("/live/stream", (ev) => { if (ev?.type?.startsWith?.("cup.")) load(); });

  if (nf) return <div className={`${CONTAINER} py-16 animate-fade-up`}><Empty label="Tie not found" /></div>;
  if (!data) return <Loader />;
  const t = data.tie;
  const homeWin = t.winner_club && t.winner_club.id === t.home_club?.id;
  const awayWin = t.winner_club && t.winner_club.id === t.away_club?.id;

  return (
    <div className="animate-fade-up pb-16">
      <div className={`${CONTAINER} pt-8`}>
        <Link to="/cup" data-testid="tie-back" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-[#E5B842] mb-6" style={{ transition: "color 0.15s ease" }}>
          <ArrowLeft className="w-4 h-4" /> Road to the Final
        </Link>

        {/* LEVEL 3 — tie match-up header */}
        <div data-testid="tie-header" className="relative overflow-hidden rounded-2xl foil-border bg-gradient-to-b from-[#161616] to-[#070707] p-6 sm:p-8 mb-6">
          <div className="absolute inset-0 mesh-grid opacity-[0.16] pointer-events-none" />
          <div className="relative flex items-center justify-between mb-5">
            <span className="font-mono text-[11px] text-[#E5B842] tracking-wider uppercase">{t.round === "DEMO" ? "Demo Tie" : t.round} · {t.two_leg ? "Two Legs" : "Single Match"}{t.is_staged_demo ? " · Staged demo" : ""}</span>
            <StatusPill status={t.status} />
          </div>
          <div className="relative grid grid-cols-[1fr_auto_1fr] items-center gap-3 sm:gap-8">
            <TieSide club={t.home_club} seed={t.home_seed} win={homeWin} align="end" />
            <div className="flex flex-col items-center">
              <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono text-center leading-tight">Aggregate<br />Team Points</span>
              <div data-testid="tie-aggregate" className="flex items-center gap-3 sm:gap-4 mt-1">
                <span className={`font-heading text-4xl sm:text-6xl font-black tabular-nums ${homeWin ? "text-[#E5B842]" : "text-slate-100"}`}>{t.aggregate_home}</span>
                <span className="text-slate-600 font-heading text-2xl">–</span>
                <span className={`font-heading text-4xl sm:text-6xl font-black tabular-nums ${awayWin ? "text-[#E5B842]" : "text-slate-100"}`}>{t.aggregate_away}</span>
              </div>
              {t.extra_duels_required && <span className="inline-flex items-center gap-1 text-[10px] font-mono text-fuchsia-400 uppercase mt-2"><Zap className="w-3 h-3" /> Decided in Extra Duels</span>}
            </div>
            <TieSide club={t.away_club} seed={t.away_seed} win={awayWin} align="start" />
          </div>
          {t.winner_club && (
            <div data-testid="tie-winner" className="relative mt-6 text-center flex items-center justify-center gap-2 text-emerald-300 font-heading font-bold uppercase tracking-wide">
              <Crown className="w-4 h-4 text-[#E5B842]" /> {t.winner_club.name} advances
            </div>
          )}
        </div>

        {/* LEG BREAKDOWN */}
        <div className="flex items-center gap-2 mb-3">
          <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-50">Leg Breakdown</h2>
          <span className="text-[10px] font-mono text-slate-600 uppercase tracking-wider">per-leg Team Points</span>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {data.legs.length === 0 && <div className="md:col-span-2"><Empty label="Legs not published yet" /></div>}
          {data.legs.map((l) => {
            const m = l.match;
            return (
              <Link key={l.leg} to={`/match/${m.id}`} data-testid={`tie-leg-${l.leg}`}
                className="block rounded-xl bg-[#0f0f0f] border border-[#2b2512] p-4 hover:border-[#E5B842]/45" style={{ transition: "border-color 0.15s ease" }}>
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono text-[11px] uppercase tracking-wider text-slate-500">{l.leg === 1 ? "First Leg" : "Second Leg"}</span>
                  <StatusPill status={m.status === "FINAL" ? "COMPLETED" : "LEG1"} />
                </div>
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-2 flex-1 min-w-0"><ClubCrest club={m.home_club} size={26} /><span className="truncate text-slate-200 text-sm font-semibold">{m.home_club?.short_name}</span></span>
                  <span className="flex items-center gap-2 shrink-0">
                    <span className="font-heading text-2xl font-black text-[#E5B842] tabular-nums">{m.home_team_points ?? "–"}</span>
                    <span className="text-[7px] leading-tight font-mono uppercase text-slate-500 text-center">Team<br />Pts</span>
                    <span className="font-heading text-2xl font-black text-[#E5B842] tabular-nums">{m.away_team_points ?? "–"}</span>
                  </span>
                  <span className="flex items-center gap-2 flex-1 min-w-0 justify-end"><span className="truncate text-slate-200 text-sm font-semibold text-right">{m.away_club?.short_name}</span><ClubCrest club={m.away_club} size={26} /></span>
                </div>
                <div className="mt-2 text-[10px] font-mono text-slate-600 text-center">Home · {m.home_club?.short_name} vs {m.away_club?.short_name} · Away</div>
              </Link>
            );
          })}
        </div>

        {/* EXTRA DUELS — ArenaBook tie-break mechanic */}
        {data.extra_sessions.length > 0 && (
          <div data-testid="tie-extra-sessions">
            <div className="flex items-center gap-2 mb-1">
              <Zap className="w-4 h-4 text-fuchsia-400" />
              <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-slate-50">ArenaBook Extra Duels</h2>
            </div>
            <p className="text-[11px] font-mono text-slate-500 mb-4 flex items-center gap-1.5">
              <Info className="w-3.5 h-3.5 text-slate-600" />
              Triggered on a level aggregate · stored separately from the 11 regulation duels · reset coupon limits: {String(data.extra_sessions[0]?.reset_coupon_limits)}
            </p>
            <div className="space-y-3">
              {data.extra_sessions.map((s) => <ExtraDuel key={s.round_number} s={s} homeName={t.home_club?.short_name} awayName={t.away_club?.short_name} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TieSide({ club, seed, win, align }) {
  return (
    <Link to={club ? `/club/${club.id}` : "#"} className={`flex flex-col items-center gap-2 ${align === "end" ? "sm:items-end" : "sm:items-start"} group`}>
      <ClubCrest club={club} size={56} />
      <div className={`flex flex-col items-center ${align === "end" ? "sm:items-end" : "sm:items-start"}`}>
        <span className={`font-heading font-black uppercase text-sm sm:text-lg text-center group-hover:text-[#E5B842] ${win ? "text-[#E5B842]" : "text-slate-100"}`} style={{ transition: "color 0.15s ease" }}>{club?.name}</span>
        {seed && seed < 90 ? <span className="font-mono text-[10px] text-slate-500">#{seed} seed</span> : null}
      </div>
    </Link>
  );
}

// WINNER comes strictly from backend settlement (winner_side) — never inferred from odds.
function ExtraDuel({ s, homeName, awayName }) {
  const d = s.duel;
  const resolved = s.status === "RESOLVED";
  const split = resolved && !s.winner_side;
  const winnerName = s.winner_side === "HOME" ? homeName : s.winner_side === "AWAY" ? awayName : null;
  return (
    <div data-testid={`extra-round-${s.round_number}`} className="rounded-xl bg-[#0f0f0f] border border-fuchsia-500/20 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 bg-fuchsia-950/20 border-b border-fuchsia-500/15">
        <span className="font-heading font-bold uppercase text-slate-100 text-sm flex items-center gap-1.5"><Swords className="w-3.5 h-3.5 text-fuchsia-400" /> Extra Duel #{s.round_number}</span>
        {resolved ? (
          split
            ? <span className="text-[10px] font-mono font-bold uppercase text-slate-400">Split → next round</span>
            : <span className="inline-flex items-center gap-1 text-[10px] font-mono font-bold uppercase text-emerald-300"><Crown className="w-3 h-3 text-[#E5B842]" /> {winnerName} wins +{s.team_points} TP</span>
        ) : <span className="text-[10px] font-mono font-bold uppercase text-amber-300">Live</span>}
      </div>
      {d && (
        <div className="grid grid-cols-2 divide-x divide-[#2b2512]">
          <CouponSide label={`Home · ${homeName}`} coupon={d.home_coupon} winning={s.winner_side === "HOME"} />
          <CouponSide label={`Away · ${awayName}`} coupon={d.away_coupon} winning={s.winner_side === "AWAY"} />
        </div>
      )}
      {d && (
        <div className="px-4 py-2 border-t border-[#2b2512] text-[10px] font-mono text-slate-500 flex items-center justify-between">
          <span>Odds are the ArenaBook coupon total — not a score</span>
          {!split && <span className="text-slate-300">Settled @ <b className="text-[#E5B842]">{d.winning_total_odds}</b></span>}
        </div>
      )}
    </div>
  );
}

function CouponSide({ label, coupon, winning }) {
  return (
    <div className={`p-4 ${winning ? "bg-[#140f02]/30" : ""}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
        {winning && <span className="text-[9px] font-mono font-bold uppercase text-emerald-300">Winner</span>}
      </div>
      <div className="flex items-baseline gap-2 mb-2">
        <span className="font-heading text-2xl font-black text-slate-100 tabular-nums">{coupon.total_odds}</span>
        <span className="text-[9px] font-mono uppercase text-slate-500">total odds · {coupon.selections.length} sel</span>
      </div>
      {coupon.selections.length > 0 && (
        <ul className="space-y-1">
          {coupon.selections.map((sel, i) => (
            <li key={i} className="text-[10px] font-mono text-slate-400 flex items-center justify-between gap-2">
              <span className="truncate">{sel.label}{sel.event_label ? ` · ${sel.event_label}` : ""}</span>
              <span className="text-slate-300 shrink-0">{sel.odds}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
